import {
  Button,
  ROW_CONCEAL_CLASS,
  ROW_REVEAL_CLASS,
  Typography,
  typographyVariants
} from '@sollapay/ui/components';
import {AccountCardIcon} from '@sollapay/ui/icons';
import {cn} from '@sollapay/ui/lib';
import {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {CopyStateIcon, useCopyToClipboard} from '../CopyButton';

/** Click copies; `masked` hides all but the last group until hover, `revealOnRowHover` swaps it in place with its table row. */
export interface TrustAccountNumberChipProps {
  refId: string;
  fieldName: string;
  size?: 'lg' | 'md' | 'sm';
  masked?: boolean;
  revealOnRowHover?: boolean;
  showIcon?: boolean;
  weight?: 'regular' | 'semibold';
  ns?: string;
}

interface SizeStyle {
  chip: string;
  gap: string;
  text: 'sm' | 'xs';
  textClass?: string;
  color?: 'secondary';
  card: string;
  icon: string;
}

const SIZE_STYLES: Record<NonNullable<TrustAccountNumberChipProps['size']>, SizeStyle> = {
  lg: {chip: 'h-6 rounded-md px-2', gap: 'gap-1.5', text: 'sm', card: 'w-5.5', icon: 'size-3'},
  md: {
    chip: 'h-6 rounded-md px-1.5',
    gap: 'gap-1',
    text: 'xs',
    color: 'secondary',
    card: 'w-4.5',
    icon: 'size-3'
  },
  sm: {
    chip: 'h-4.5 rounded px-1.5',
    gap: 'gap-1',
    text: 'xs',
    textClass: 'text-caption',
    color: 'secondary',
    card: 'w-4',
    icon: 'size-2.5'
  }
};

// size-auto opts out of Button's forced svg size so the configured width applies.
const CARD_ICON = 'size-auto shrink-0';
const REVEALED = 'hidden group-hover:inline group-focus-visible:inline group-data-revealed:inline';
const CONCEALED = 'group-hover:hidden group-focus-visible:hidden group-data-revealed:hidden';
const FULL_NUMBER = `${REVEALED} text-brand-accent`;
const STACKED = 'col-start-1 row-start-1 justify-self-start';
// Typography would pop its overflow tooltip while the label is collapsed, so labels only borrow its styles.
const LABEL_CLASS = typographyVariants({size: 'xs', color: 'tertiary'});
const ACCENT_ON_HOVER =
  'transition-colors duration-200 motion-reduce:transition-none group-hover:text-brand-accent group-focus-visible:text-brand-accent';
const GROW_ICON =
  'transition-transform duration-200 motion-reduce:transition-none group-hover:scale-108 group-focus-visible:scale-108 group-data-revealed:scale-108';
// -me-1 cancels the extra flex gap the collapsed label would otherwise leave.
const EXPAND_LABEL =
  'flex items-center leading-none max-w-0 -me-1 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-200 motion-reduce:transition-none group-hover:max-w-24 group-hover:me-1 group-hover:opacity-100 group-focus-visible:max-w-24 group-focus-visible:me-1 group-focus-visible:opacity-100 group-data-revealed:max-w-24 group-data-revealed:me-1 group-data-revealed:opacity-100';

export const TrustAccountNumberChip = ({
  refId,
  fieldName,
  size = 'lg',
  masked = false,
  revealOnRowHover = false,
  showIcon = true,
  weight = 'semibold',
  ns = 'common'
}: TrustAccountNumberChipProps) => {
  const {t} = useTranslation(ns);
  const {copy, copied} = useCopyToClipboard(ns);
  const [revealed, setRevealed] = useState(false);
  // Hover capability misreports on hybrid devices, so the reveal-before-copy step follows the actual pointer.
  const pointerType = useRef<string | null>(null);
  const styles = SIZE_STYLES[size];
  const parts = refId.split('-');
  const mode: RevealMode = !masked ? 'none' : revealOnRowHover ? 'row' : 'hover';

  const handleClick = () => {
    const touched = pointerType.current === 'touch' || pointerType.current === 'pen';
    pointerType.current = null;
    if (mode === 'hover' && !revealed && touched) {
      setRevealed(true);
      return;
    }
    void copy(refId, {fieldName});
  };

  return (
    <Button
      data-slot="trust-account-number"
      data-revealed={revealed || undefined}
      variant="tertiary"
      size={null}
      aria-label={copied ? t('copied') : `${t('copy')} ${refId}`}
      className={cn(
        'group w-fit shrink-0 self-start bg-transparent hover:bg-transparent',
        styles.chip,
        !showIcon && 'ps-1'
      )}
      // Often sits inside clickable rows and cards — copying must not also open them.
      onClick={e => {
        e.stopPropagation();
        handleClick();
      }}
      onPointerDown={e => {
        pointerType.current = e.pointerType;
      }}
      onKeyDown={e => {
        e.stopPropagation();
        pointerType.current = null;
      }}
      onBlur={masked ? () => setRevealed(false) : undefined}
    >
      <span className="grid">
        <ChipContent
          parts={parts}
          fieldName={fieldName}
          styles={styles}
          mode={mode}
          showIcon={showIcon}
          weight={weight}
          copied={copied}
        />
        {mode === 'hover' && (
          <RevealedWidthSizer parts={parts} fieldName={fieldName} styles={styles} weight={weight} />
        )}
      </span>
    </Button>
  );
};

type RevealMode = 'none' | 'hover' | 'row';

const COPY_ICON_CLASS: Record<RevealMode, string | undefined> = {
  none: undefined,
  hover: REVEALED,
  row: ROW_REVEAL_CLASS
};

interface ChipContentProps {
  parts: string[];
  fieldName: string;
  styles: SizeStyle;
  mode: RevealMode;
  showIcon: boolean;
  weight: NonNullable<TrustAccountNumberChipProps['weight']>;
  copied: boolean;
}

const ChipContent = ({
  parts,
  fieldName,
  styles,
  mode,
  showIcon,
  weight,
  copied
}: ChipContentProps) => (
  <span className={cn('col-start-1 row-start-1 flex items-center', styles.gap)}>
    {showIcon && (
      <AccountCardIcon
        data-slot="icon"
        className={cn(CARD_ICON, styles.card, mode === 'hover' && GROW_ICON)}
      />
    )}
    {/* Baseline, not centre: the mono number and the body-font label have different glyph boxes. */}
    <span className={cn('flex items-baseline', styles.gap)}>
      {mode !== 'none' && (
        <span
          data-slot="label"
          className={cn(
            LABEL_CLASS,
            styles.textClass,
            mode === 'hover' ? ['text-clip', EXPAND_LABEL] : 'leading-none'
          )}
        >
          {fieldName}
        </span>
      )}
      <Typography
        as="span"
        size={styles.text}
        weight={weight}
        color={styles.color}
        className={cn('font-mono leading-none', styles.textClass, mode === 'row' && 'grid')}
      >
        <NumberParts parts={parts} mode={mode} />
      </Typography>
    </span>
    <CopyStateIcon
      copied={copied}
      className={cn(styles.icon, 'text-fg-tertiary', COPY_ICON_CLASS[mode])}
    />
  </span>
);

// In row mode both forms share one grid cell, so swapping them never changes the chip's width.
const NumberParts = ({parts, mode}: {parts: string[]; mode: RevealMode}) => {
  if (mode === 'none') return <GroupedNumber parts={parts} className={ACCENT_ON_HOVER} />;
  if (mode === 'hover') {
    return (
      <>
        <MaskedNumber parts={parts} className={CONCEALED} />
        <GroupedNumber parts={parts} className={FULL_NUMBER} />
      </>
    );
  }
  return (
    <>
      <MaskedNumber parts={parts} className={cn(STACKED, ROW_CONCEAL_CLASS)} />
      <GroupedNumber parts={parts} className={cn(STACKED, 'font-bold text-fg', ROW_REVEAL_CLASS)} />
    </>
  );
};

interface RevealedWidthSizerProps {
  parts: string[];
  fieldName: string;
  styles: SizeStyle;
  weight: NonNullable<TrustAccountNumberChipProps['weight']>;
}

// Occupies the revealed width in the same grid cell so revealing never resizes the table column.
const RevealedWidthSizer = ({parts, fieldName, styles, weight}: RevealedWidthSizerProps) => (
  <span
    aria-hidden
    className={cn(
      'pointer-events-none invisible col-start-1 row-start-1 flex items-center',
      styles.gap
    )}
  >
    <AccountCardIcon className={cn(CARD_ICON, styles.card)} />
    <span className={cn(LABEL_CLASS, 'me-1 text-clip', styles.textClass)}>{fieldName}</span>
    <Typography
      as="span"
      size={styles.text}
      weight={weight}
      dir="ltr"
      className={cn('font-mono leading-none', styles.textClass)}
    >
      <GroupedNumber parts={parts} />
    </Typography>
    <span className={cn('shrink-0', styles.icon)} />
  </span>
);

interface NumberPartProps {
  parts: string[];
  className?: string;
}

const GroupedNumber = ({parts, className}: NumberPartProps) => (
  <span data-slot="number" dir="ltr" className={className}>
    {parts.join('-')}
  </span>
);

const MaskedNumber = ({parts, className}: NumberPartProps) => (
  <span data-slot="masked-number" dir="ltr" className={className}>
    <span aria-hidden className="text-fg-disabled">
      ••••
    </span>{' '}
    {parts.at(-1)}
  </span>
);
